import React, { useEffect, useMemo, useState } from 'react';
import { BarList, Btn, Chip, DataTable, Loading, Msg, Section, Select, TextInput, fmtDate, useAdminGet } from '../kit';
import { IPINTEL_EVENTS } from '../../../landing-page/components/ipintel/track';
import { Pager, S, ExportButtons, num, pageInfo, rowsOf, str, useDebounced } from './shared';

/**
 * Activity — the page's own click stream: every site_events row whose action
 * starts with ipintel_ (GET ip_intel/activity), with counts per action that
 * double as the action filter. The names are the ones the public page sends
 * through utils/analytics trackClick.
 */

// The labels come from the public page's own catalogue (IPINTEL_EVENTS in
// landing-page/components/ipintel/track.ts), so a new ipintel_* action is
// labelled here the moment the page starts sending it. The raw name shows
// under each label in the events table.
export const ACTIONS: Record<string, string> = { ...IPINTEL_EVENTS };

/** A name the catalogue does not know (an old or future one) reads by its family; else raw. */
const FAMILIES: [RegExp, string][] = [
  [/^ipintel_bulk_(.+)$/, 'Bulk form'], [/^ipintel_lead_(.+)$/, 'Sample form'],
  [/^ipintel_sample_(.+)$/, 'Sample'], [/^ipintel_(?:payment|checkout)_(.+)$/, 'Payment'],
  [/^ipintel_intake_(.+)$/, 'Intake'], [/^ipintel_(?:login|signup)_(.+)$/, 'Login'],
  [/^ipintel_nav_(.+)$/, 'Nav'], [/^ipintel_click_(.+)$/, 'Clicked'],
];

export const actionLabel = (a: string): string => {
  if (ACTIONS[a]) return ACTIONS[a];
  for (const [re, fam] of FAMILIES) {
    const m = re.exec(a);
    if (m) return `${fam}: ${m[1].replace(/_/g, ' ')}`;
  }
  return a;
};

const COLS = ['at', 'action', 'props', 'user_id', 'country', 'device', 'utm_source', 'path', 'visitor_id', 'session_id'];
const LABELS: Record<string, string> = {
  at: 'When', action: 'Action', props: 'Details', path: 'Page', user_id: 'Account', country: 'Country',
  device: 'Device', utm_source: 'UTM source', visitor_id: 'Visitor', session_id: 'Session',
};

const propsText = (p: any): string => {
  if (!p) return '';
  let o = p;
  if (typeof p === 'string') { try { o = JSON.parse(p); } catch { return p; } }
  if (typeof o !== 'object') return String(o);
  return Object.entries(o)
    .filter(([, v]) => v !== null && v !== undefined && v !== '')
    .map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`).join(' · ');
};

// The server windows events by days (ip_intel._events_where), like the Overview.
type Filters = { q: string; action: string; days: string; team: string };
const EMPTY: Filters = { q: '', action: '', days: '30', team: 'exclude' };
const DAY_LABELS: Record<string, string> = {
  '1': 'Last 24 hours', '7': 'Last 7 days', '30': 'Last 30 days', '90': 'Last 90 days', '365': 'Last 12 months',
};

const Activity: React.FC = () => {
  const [f, setF] = useState<Filters>(EMPTY);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState('100');
  const [tick, setTick] = useState(0);
  const [dlErr, setDlErr] = useState('');
  const q = useDebounced(f.q);
  const set = (k: keyof Filters) => (v: string) => setF(s => ({ ...s, [k]: v }));

  const filterParams = useMemo(() => ({ q, action: f.action, days: f.days, team: f.team }),
    [q, f.action, f.days, f.team]);
  const filterKey = JSON.stringify(filterParams);
  useEffect(() => { setPage(0); }, [filterKey, perPage]);

  const res = useAdminGet(S, 'activity', { ...filterParams, page, per_page: perPage },
    [filterKey, page, perPage, tick]);
  const d = res.data || {};
  const raw = rowsOf(d, 'events');
  const per = Number(perPage) || 100;
  const { total, pages } = pageInfo(d, raw, per);

  // Counts by action: [{action, count, visitors?}] or {action: count}.
  const counts = useMemo(() => {
    const v = d.counts ?? d.by_action ?? d.actions;
    const arr: { action: string; count: number; visitors?: number }[] = Array.isArray(v)
      ? v.map((x: any) => ({ action: str(x.action ?? x.key), count: num(x.count ?? x.n), visitors: x.visitors }))
      : (v && typeof v === 'object')
        ? Object.entries(v).map(([action, n]) => ({ action, count: num(n) })) : [];
    return arr.filter(x => x.action).sort((a, b) => b.count - a.count);
  }, [d]);
  const hasVisitors = counts.some(c => c.visitors !== undefined && c.visitors !== null);
  // Bar labels double as React keys and as the filter handle, so two actions
  // that read the same (a catalogue label and a family guess) must not collide.
  const barLabel = useMemo(() => {
    const out: Record<string, string> = {};
    const used = new Set<string>();
    counts.forEach(c => {
      let l = actionLabel(c.action);
      if (used.has(l)) l = `${l} (${c.action.replace(/^ipintel_/, '')})`;
      used.add(l); out[c.action] = l;
    });
    return out;
  }, [counts]);
  const barOf = (a: string) => barLabel[a] || actionLabel(a);

  const rows = useMemo(() => raw.map(r => ({ ...r, props: propsText(r.props) })), [raw]);


  const toggleAction = (a: string) => setF(s => ({ ...s, action: s.action === a ? '' : a }));

  const chips: { label: string; value: string; clear: () => void }[] = [];
  if (f.q) chips.push({ label: 'Search', value: f.q, clear: () => set('q')('') });
  if (f.action) chips.push({ label: 'Action', value: actionLabel(f.action), clear: () => set('action')('') });
  if (f.days !== EMPTY.days) chips.push({ label: 'Window', value: DAY_LABELS[f.days] || `${f.days} days`, clear: () => set('days')(EMPTY.days) });
  if (f.team !== EMPTY.team) chips.push({ label: 'Team', value: 'included', clear: () => set('team')(EMPTY.team) });

  const actionOptions = Array.from(new Set(['', ...Object.keys(ACTIONS), ...counts.map(c => c.action)]));

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Section title="Clicks by action"
          right={<span className="text-xs text-grayscale-1100">{(DAY_LABELS[f.days] || `${f.days} days`).toLowerCase()} · click one to filter</span>}>
          {res.loading && !res.data ? <Loading /> : counts.length === 0 ? (
            <div className="text-sm text-grayscale-1100 py-4 text-center">No IP Intel events recorded yet.</div>
          ) : (
            <BarList items={counts.map(c => ({ label: barOf(c.action), value: c.count }))}
              onSelect={l => {
                const c = counts.find(x => barOf(x.action) === l);
                if (c) toggleAction(c.action);
              }}
              activeLabel={f.action ? barOf(f.action) : undefined} />
          )}
        </Section>
        {hasVisitors ? (
          <Section title="Distinct visitors by action">
            <BarList items={counts.filter(c => c.visitors !== undefined)
              .map(c => ({ label: barOf(c.action), value: num(c.visitors) }))} />
          </Section>
        ) : (
          <Section title="What is tracked">
            <div className="text-xs text-grayscale-1100 flex flex-col gap-1 max-h-72 overflow-y-auto">
              {Object.entries(ACTIONS).map(([k, v]) => (
                <div key={k} className="flex gap-2">
                  <code className="text-[11px] text-grayscale-1300 w-56 shrink-0">{k}</code><span>{v}</span>
                </div>
              ))}
            </div>
          </Section>
        )}
      </div>

      <Section title="Events"
        right={
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-grayscale-1100">{total.toLocaleString()} event{total === 1 ? '' : 's'}</span>
            {chips.length > 0 && <Btn small onClick={() => setF(EMPTY)}>Clear {chips.length} filter{chips.length === 1 ? '' : 's'}</Btn>}
            <Btn small onClick={() => setTick(t => t + 1)} disabled={res.loading}>Refresh</Btn>
            <ExportButtons kind="activity" params={filterParams} onError={setDlErr} what="These events" />
          </div>
        }>
        <div className="flex items-center gap-2 flex-wrap">
          <TextInput value={f.q} onChange={set('q')} wide placeholder="Search visitor, session, account or details…" />
          <Select value={f.action} onChange={set('action')} options={actionOptions}
            labels={{ '': 'Any action', ...Object.fromEntries(actionOptions.filter(Boolean).map(a => [a, actionLabel(a)])) }} />
          <Select value={f.days} onChange={set('days')} options={Object.keys(DAY_LABELS)} labels={DAY_LABELS} />
          <Select value={f.team} onChange={set('team')} options={['exclude', 'include']}
            labels={{ exclude: 'Without team & bots', include: 'Including team & bots' }} />
        </div>
        {chips.length > 0 && (
          <div className="flex gap-1.5 flex-wrap">
            {chips.map(c => <Chip key={c.label} label={c.label} value={c.value} onClear={c.clear} />)}
          </div>
        )}
        {dlErr && <Msg kind="error">Export failed: {dlErr}</Msg>}
        {res.error && <Msg kind="error">{res.error}</Msg>}
        {res.loading && !res.data ? <Loading /> : (
          <DataTable rows={rows} columns={COLS} labels={LABELS} maxHeight="40rem"
            empty={chips.length ? 'No events match these filters.' : 'No IP Intel events recorded yet.'}
            render={{
              at: r => <span className="text-xs" title={r.at}>{fmtDate(r.at)}</span>,
              action: r => (
                <span className="flex flex-col">
                  <span className="text-grayscale-1300">{actionLabel(str(r.action))}</span>
                  <code className="text-[10px] text-grayscale-1100">{r.action}</code>
                </span>
              ),
              props: r => <span className="text-xs text-grayscale-1200" title={r.props}>{r.props || '—'}</span>,
              user_id: r => r.email ? <span className="text-xs" title={str(r.user_id)}>{r.email}</span>
                : r.user_id ? <span className="font-mono text-[11px]" title={r.user_id}>{str(r.user_id).slice(0, 10)}…</span> : 'anon',
              visitor_id: r => r.visitor_id
                ? <button type="button" className="font-mono text-[11px] underline underline-offset-2"
                    title={`Show only visitor ${r.visitor_id}`} onClick={() => set('q')(str(r.visitor_id))}>
                    {str(r.visitor_id).slice(0, 10)}…
                  </button> : '—',
              session_id: r => r.session_id ? <span className="font-mono text-[11px]" title={r.session_id}>{str(r.session_id).slice(0, 10)}…</span> : '—',
            }} />
        )}
        {pages > 1 && (
          <Pager page={page} pages={pages} total={total} shown={raw.length} per={per}
            onPage={setPage} perPage={perPage} onPerPage={setPerPage} />
        )}
      </Section>
    </div>
  );
};

export default Activity;
